/**
 * Apotheca Profile Client
 * ───────────────────────
 * Unified interface for reading/writing the Apotheca customer profile.
 * Transparently merges two storage layers:
 *
 *   1. Supabase (via Shopify App Proxy at /apps/gabriel/profile)
 *      - Only for logged-in customers
 *      - Only NON-PHI fields (name, email, phone, address, state, sex,
 *        preferences, Shopify customer ID)
 *      - Cross-device sync
 *
 *   2. Browser cookie (apotheca_health_profile_draft)
 *      - Works for guests AND logged-in users
 *      - Holds PHI fields (dob, weight, medications, allergies, conditions)
 *      - Phase 1 only — gets migrated to HIPAA Supabase project post-launch
 *
 * Public API:
 *   ApothecaProfile.load()              → Promise<profile | null>
 *   ApothecaProfile.save(patch)         → Promise<profile>
 *   ApothecaProfile.clear()             → void (local only; does not delete server row)
 *   ApothecaProfile.isLoggedIn()        → boolean
 *   ApothecaProfile.onChange(callback)  → unsubscribe()
 *
 * The unified profile shape returned by load():
 *   {
 *     // Non-PHI (from Supabase when logged in, from cookie otherwise)
 *     first_name, last_name, email, phone, biological_sex,
 *     address_line1, address_line2, city, state, zip, country,
 *     preferred_units, marketing_opt_in,
 *     // PHI (always from cookie in Phase 1)
 *     dob, weight_lbs, medications[], allergies[], conditions[],
 *     // Meta
 *     _source: 'supabase+cookie' | 'cookie' | 'supabase' | 'empty',
 *     _logged_in: boolean,
 *   }
 */

(function() {
  'use strict';

  const COOKIE_NAME = 'apotheca_health_profile_draft';
  const PROXY_URL = '/apps/gabriel/profile';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

  // Non-PHI fields — synced to Supabase
  const NON_PHI_FIELDS = [
    'first_name', 'last_name', 'email', 'phone',
    'biological_sex',
    'address_line1', 'address_line2', 'city', 'state', 'zip', 'country',
    'preferred_units', 'marketing_opt_in',
    'terms_version', 'terms_accepted_at',
  ];

  // PHI fields — cookie only (Phase 1)
  const PHI_FIELDS = [
    'dob', 'weight_lbs',
    'medications', 'allergies', 'conditions',
  ];

  const listeners = [];

  // ─── Shopify customer detection ──────────────────────────────────────
  function isLoggedIn() {
    // Set by the Liquid template via window.__apothecaCustomer = {{ customer.id }}
    return typeof window.__apothecaCustomer === 'object'
      && window.__apothecaCustomer !== null
      && !!window.__apothecaCustomer.id;
  }

  // ─── Cookie layer ────────────────────────────────────────────────────
  function readCookie() {
    const match = document.cookie.match(
      new RegExp('(^|; )' + COOKIE_NAME + '=([^;]*)')
    );
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch(e) {
      return null;
    }
  }

  function writeCookie(data) {
    try {
      const encoded = encodeURIComponent(JSON.stringify(data));
      document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    } catch(e) {
      console.warn('[ApothecaProfile] cookie write failed:', e);
    }
  }

  function clearCookie() {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
  }

  // ─── Supabase layer (via App Proxy) ──────────────────────────────────
  async function fetchFromSupabase(source) {
    if (!isLoggedIn()) return null;
    try {
      const url = PROXY_URL + '?source=' + encodeURIComponent(source || 'unknown');
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      if (!resp.ok) {
        console.warn('[ApothecaProfile] Supabase fetch failed:', resp.status);
        return null;
      }
      const data = await resp.json();
      return data && data.profile ? data.profile : null;
    } catch(e) {
      console.warn('[ApothecaProfile] Supabase fetch error:', e);
      return null;
    }
  }

  async function pushToSupabase(patch, source) {
    if (!isLoggedIn()) return null;
    // Strip PHI before sending
    const cleanPatch = {};
    for (const key of NON_PHI_FIELDS) {
      if (patch[key] !== undefined) cleanPatch[key] = patch[key];
    }
    if (Object.keys(cleanPatch).length === 0) return null;

    try {
      const url = PROXY_URL + '?source=' + encodeURIComponent(source || 'unknown');
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(cleanPatch),
      });
      if (!resp.ok) {
        console.warn('[ApothecaProfile] Supabase push failed:', resp.status);
        return null;
      }
      const data = await resp.json();
      return data && data.profile ? data.profile : null;
    } catch(e) {
      console.warn('[ApothecaProfile] Supabase push error:', e);
      return null;
    }
  }

  // ─── Merge layer ─────────────────────────────────────────────────────
  // Fold Supabase profile + cookie into one unified object.
  function mergeProfile(supabaseProfile, cookieProfile) {
    if (!supabaseProfile && !cookieProfile) {
      return { _source: 'empty', _logged_in: isLoggedIn() };
    }
    const merged = {};
    // Non-PHI: Supabase wins if present, cookie fallback
    for (const key of NON_PHI_FIELDS) {
      if (supabaseProfile && supabaseProfile[key] != null && supabaseProfile[key] !== '') {
        merged[key] = supabaseProfile[key];
      } else if (cookieProfile && cookieProfile[key] != null && cookieProfile[key] !== '') {
        merged[key] = cookieProfile[key];
      }
    }
    // PHI: cookie only (Phase 1)
    for (const key of PHI_FIELDS) {
      if (cookieProfile && cookieProfile[key] != null) {
        merged[key] = cookieProfile[key];
      }
    }
    merged._source = supabaseProfile && cookieProfile ? 'supabase+cookie'
                   : supabaseProfile ? 'supabase'
                   : 'cookie';
    merged._logged_in = isLoggedIn();
    return merged;
  }

  // ─── Public: load the unified profile ────────────────────────────────
  async function load(source) {
    const cookieProfile = readCookie();
    const supabaseProfile = await fetchFromSupabase(source);

    // If Supabase returned something the cookie doesn't have, write it back
    // to the cookie so offline + other scripts can read it instantly.
    if (supabaseProfile) {
      const next = Object.assign({}, cookieProfile || {});
      let changed = false;
      for (const key of NON_PHI_FIELDS) {
        if (supabaseProfile[key] != null && next[key] !== supabaseProfile[key]) {
          next[key] = supabaseProfile[key];
          changed = true;
        }
      }
      if (changed) writeCookie(next);
    }

    return mergeProfile(supabaseProfile, cookieProfile);
  }

  // ─── Public: save a patch ─────────────────────────────────────────────
  // Writes non-PHI to Supabase AND cookie; writes PHI to cookie only.
  async function save(patch, source) {
    // Always write to cookie first (fastest, works offline)
    const existing = readCookie() || {};
    const merged = Object.assign({}, existing, patch);
    writeCookie(merged);

    // Fire-and-forget push to Supabase for non-PHI
    if (isLoggedIn()) {
      try {
        await pushToSupabase(patch, source);
      } catch(e) {
        // Already logged; cookie write is authoritative for this session
      }
    }

    // Notify listeners
    for (const cb of listeners) {
      try { cb(merged); } catch(e) { /* no-op */ }
    }

    return merged;
  }

  // ─── Public: subscribe to changes ────────────────────────────────────
  function onChange(callback) {
    listeners.push(callback);
    return function unsubscribe() {
      const idx = listeners.indexOf(callback);
      if (idx >= 0) listeners.splice(idx, 1);
    };
  }

  // ─── Expose ──────────────────────────────────────────────────────────
  window.ApothecaProfile = {
    load,
    save,
    clear: clearCookie,
    isLoggedIn,
    _readCookie: readCookie, // for debugging
    _NON_PHI_FIELDS: NON_PHI_FIELDS,
    _PHI_FIELDS: PHI_FIELDS,
  };
})();
